import { useState } from 'react';
import { Search, Download, UserCheck } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { format } from 'date-fns';

const MOCK_VISITORS = [
  { id: 'v-001', name: 'Rahul Gupta', phone: '9876500001', host: 'Priya Mehta', purpose: 'Client meeting', check_in: '10:15', check_out: '12:30', device: 'Lens – Entrance', date: '2026-06-11', status: 'CHECKED_OUT' },
  { id: 'v-002', name: 'Anjali Sharma', phone: '9876500002', host: 'Rohan Sharma', purpose: 'Interview', check_in: '11:00', check_out: null, device: 'Lens – Entrance', date: '2026-06-11', status: 'INSIDE' },
  { id: 'v-003', name: 'Vikram Nair', phone: '9876500003', host: 'Arjun Patel', purpose: 'Vendor demo', check_in: '14:00', check_out: '15:45', device: 'Lens – Entrance', date: '2026-06-11', status: 'CHECKED_OUT' },
  { id: 'v-004', name: 'Geeta Pillai', phone: '9876500004', host: 'Sneha Reddy', purpose: 'Personal', check_in: '09:30', check_out: '10:00', device: 'Lens – Entrance', date: '2026-06-10', status: 'CHECKED_OUT' },
];

export default function VisitorsPage() {
  const [search, setSearch] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const visitors = MOCK_VISITORS.filter(v =>
    v.date === date &&
    (!search || v.name.toLowerCase().includes(search.toLowerCase()) || v.host.toLowerCase().includes(search.toLowerCase()))
  );

  const inside = visitors.filter(v => v.status === 'INSIDE').length;

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title="Visitor Log"
        subtitle={`${inside} visitor${inside !== 1 ? 's' : ''} currently inside`}
        breadcrumb={[{ label: 'Lens', href: '/lens' }, { label: 'Visitor Log' }]}
        actions={<Button variant="outline" size="sm" icon={Download}>Export</Button>}
      />

      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total Today', value: visitors.length },
          { label: 'Currently Inside', value: inside },
          { label: 'Checked Out', value: visitors.filter(v => v.status === 'CHECKED_OUT').length },
        ].map(({ label, value }) => (
          <div key={label} className="card text-center p-5">
            <p className="text-[24px] font-black text-[var(--text-primary)]">{value}</p>
            <p className="text-[12px] text-[var(--text-secondary)]">{label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]"/>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search visitor or host…"
            className="w-full pl-8 pr-3 py-2 text-[13px] border border-[var(--border)] rounded-[8px] bg-[var(--bg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40 text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"/>
        </div>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[8px] text-[13px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40"/>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full">
          <thead><tr className="border-b border-[var(--border)]">
            {['Visitor','Host','Purpose','Check In','Check Out','Device','Status'].map(h => (
              <th key={h} className="text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-5 py-3">{h}</th>
            ))}
          </tr></thead>
          <tbody className="divide-y divide-[var(--border)]">
            {visitors.map(v => (
              <tr key={v.id} className="hover:bg-[var(--bg-muted)]">
                <td className="px-5 py-3.5">
                  <p className="text-[13px] font-semibold text-[var(--text-primary)]">{v.name}</p>
                  <p className="text-[11px] text-[var(--text-tertiary)]">{v.phone}</p>
                </td>
                <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{v.host}</td>
                <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{v.purpose}</td>
                <td className="px-5 py-3.5 text-[13px] font-medium text-[var(--text-primary)]">{v.check_in}</td>
                <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{v.check_out || '—'}</td>
                <td className="px-5 py-3.5 text-[12px] text-[var(--text-tertiary)]">{v.device}</td>
                <td className="px-5 py-3.5">
                  {v.status === 'INSIDE'
                    ? <span className="flex items-center gap-1 text-[11px] font-semibold text-green-600"><UserCheck size={11}/> Inside</span>
                    : <StatusBadge status={v.status}/>}
                </td>
              </tr>
            ))}
            {visitors.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-[14px] text-[var(--text-tertiary)]">No visitors for this date</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
